import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mapResponse } from '@ngrx/operators';
import { exhaustMap, switchMap, tap } from 'rxjs';

import { TenantsApi } from '../api/tenants.api';
import { TenantsActions } from './tenants.actions';

/** Unpacks the web service's ExceptionRes envelope ('200' = displayable). */
function toMessage(error: unknown): string {
  const httpError = error as {
    error?: { responseCode?: string; responseMessage?: string };
    message?: string;
  };
  if (httpError?.error?.responseMessage) {
    return httpError.error.responseMessage;
  }
  return httpError?.message ?? 'Something went wrong. Please try again.';
}

@Injectable()
export class TenantsEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(TenantsApi);
  private readonly snackBar = inject(MatSnackBar);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.load),
      switchMap(() =>
        this.api.getTenants().pipe(
          mapResponse({
            next: (res) => TenantsActions.loadSuccess({ tenants: res.tenants, env: res.env }),
            error: (error) => TenantsActions.loadFailure({ error: toMessage(error) }),
          }),
        ),
      ),
    ),
  );

  save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.saveTenant),
      exhaustMap(({ tenantId, patch }) =>
        this.api.updateTenant(tenantId, patch).pipe(
          mapResponse({
            next: (tenant) => TenantsActions.saveTenantSuccess({ tenant }),
            error: (error) => TenantsActions.saveTenantFailure({ error: toMessage(error) }),
          }),
        ),
      ),
    ),
  );

  notifySaved$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TenantsActions.saveTenantSuccess),
        tap(({ tenant }) =>
          this.snackBar.open(`Tenant '${tenant.tenantId}' saved.`, undefined, { duration: 3000 }),
        ),
      ),
    { dispatch: false },
  );

  notifySaveFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TenantsActions.saveTenantFailure),
        tap(({ error }) => this.snackBar.open(error, 'Dismiss', { duration: 6000 })),
      ),
    { dispatch: false },
  );
}
